import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export interface Race {
  id: number;
  race_id: string;
  meeting_id: string;
  race_number: number;
  race_name: string | null;
  distance: number | null;
  class: string | null;
  prize_money: number | null;
  race_time: string | null;
  track_condition: string | null;
}

export function useMeetingRaces(meetingId: string) {
  return useQuery({
    queryKey: ['meeting-races', meetingId],
    queryFn: () => apiClient<Race[]>(`/meetings/${meetingId}/races`),
  });
}

export interface ScratchedSlot {
  analysis_type_id: number;
  count: number;
}

export interface RaceStatus {
  race_id: string;
  race_number: number;
  tips_available: number;
  results_entered: number;
  results_complete: boolean;
  // Which slot(s) had a tip scratched (pick_order 999) for this race, and how
  // many in each - across every slot, not just the one currently displayed.
  scratched_by_slot?: ScratchedSlot[] | null;
}

interface RacesStatusResponse {
  success: boolean;
  data: RaceStatus[];
}

/**
 * GET /meetings/:meetingId/races-status only returns races that already have
 * tips (backend HAVING clause) — a race missing from this map hasn't been
 * analysed yet. Used to drive the drill-down table's status column so staff
 * can see at a glance which races still need a run, without a click-through.
 */
export function useRacesStatus(meetingId: string) {
  return useQuery({
    queryKey: ['meeting-races-status', meetingId],
    queryFn: async (): Promise<Map<number, RaceStatus>> => {
      const res = await apiClient<RacesStatusResponse>(`/meetings/${meetingId}/races-status`);
      return new Map(res.data.map((r) => [r.race_number, r]));
    },
  });
}

export function useRequeueRaceForm(meetingId: string) {
  return useMutation({
    mutationFn: (raceId: string) =>
      apiClient<{ success: boolean; updated: number }>(`/meetings/${meetingId}/races/${raceId}/requeue-form`, {
        method: 'POST',
      }),
  });
}
